import {
	CheckCircle,
	Download,
	Edit,
	Eye,
	FileText,
	Plus,
	Trash2,
} from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import useConfirmDialog from "../hooks/useConfirmDialog";
import { toChecklistItem } from "../lib/walletSnake";
import type { FeeStructure, OnboardingStatus, Wallet } from "../types";
import { uploadFileToS3 } from "../utils/fileUpload";
import { notifications } from "../utils/notifications";
import ChecklistItemComponent from "./ChecklistItemComponent";
import PartnerActivationModal from "./PartnerActivationModal";
import { Button } from "./ui/button";

interface OnboardingWorkflowProps {
	wallet: Wallet;
	isUpdating?: boolean;
	handleChecklistToggle: (key: string, item: unknown) => void;
	handleSubtaskToggle: (key: string, field: "sent" | "received") => void;
	handleDocumentUpload: (key: string, file: File) => void;
	updateWallet: (id: string, updates: Record<string, unknown>) => void;
	handleOnboardingStatusChange: (status: OnboardingStatus) => void;
	handleDeleteSalesforceCase: () => void;
	isPartnerView?: boolean;
	viewingStep?: OnboardingStatus;
}

const OnboardingWorkflow: React.FC<OnboardingWorkflowProps> = ({
	wallet,
	isUpdating = false,
	handleChecklistToggle,
	handleDocumentUpload,
	updateWallet,
	handleDeleteSalesforceCase,
	isPartnerView = false,
	viewingStep: viewingStepProp,
}) => {
	const navigate = useNavigate();
	const { user } = useAuth();
	const { confirm } = useConfirmDialog();
	const [showFeeForm, setShowFeeForm] = useState(false);
	const [feeStart, setFeeStart] = useState("");
	const [feeEnd, setFeeEnd] = useState("");
	const [feeAmount, setFeeAmount] = useState("");
	const [feeType, setFeeType] = useState<"percentage" | "flat">("percentage");
	const [showActivationModal, setShowActivationModal] = useState(false);
	const [editingFeeIndex, setEditingFeeIndex] = useState<number | null>(null);

	// Local state for instant UI feedback on hasOwnFees toggle
	const [localHasOwnFees, setLocalHasOwnFees] = useState(wallet.has_own_fees);

	// Parent owns viewing step; fallback to wallet status when not yet synced
	const viewingStep = viewingStepProp ?? wallet.onboarding_status;

	// Sync local state when wallet updates
	React.useEffect(() => {
		setLocalHasOwnFees(wallet.has_own_fees);
	}, [wallet.has_own_fees]);

	const [salesforceCaseInput, setSalesforceCaseInput] = useState(
		wallet.salesforce_case_number || "",
	);

	// Handler for hasOwnFees toggle with optimistic update
	const handleHasOwnFeesChange = (value: boolean) => {
		setLocalHasOwnFees(value); // Instant UI update
		updateWallet(wallet.id, { hasOwnFees: value }); // API call in background
	};

	// Handler for document uploads that auto-completes tasks and updates status
	const handleDocumentUploadWithCompletion = async (
		checklistKey: string,
		file: File,
	) => {
		// Call the original upload handler
		handleDocumentUpload(checklistKey, file);

		// Upload to S3 and auto-complete the task
		try {
			const result = await uploadFileToS3(file);
			const current = wallet[checklistKey as keyof Wallet] as
				| Record<string, unknown>
				| undefined;
			updateWallet(wallet.id, {
				[checklistKey]: {
					...(current && typeof current === "object" ? current : {}),
					completed: true,
					completed_at: new Date().toISOString(),
					document_url: result.key,
					document_name: file.name,
					uploaded_at: new Date().toISOString(),
				},
			});
		} catch (err) {
			notifications.error(err instanceof Error ? err.message : "Upload failed");
		}
	};

	// Handler for deleting uploaded documents
	const handleDocumentDelete = async (checklistKey: string) => {
		const ok = await confirm({
			title: "Delete Document",
			message:
				"Are you sure you want to delete this document? The task will be marked as incomplete.",
			confirmLabel: "Delete",
			variant: "danger",
		});
		if (!ok) return;

		const current = wallet[checklistKey as keyof Wallet] as
			| Record<string, unknown>
			| undefined;
		updateWallet(wallet.id, {
			[checklistKey]: {
				...(current && typeof current === "object" ? current : {}),
				completed: false,
				completed_at: undefined,
				document_url: undefined,
				document_name: undefined,
				uploaded_at: undefined,
			},
		});
	};

	const handleSalesforceCaseSave = () => {
		if (!salesforceCaseInput.trim()) {
			notifications.error("Please enter a Case Number");
			return;
		}

		updateWallet(wallet.id, {
			salesforce_case_number: salesforceCaseInput.trim(),
			salesforce_case_created: {
				...(wallet.salesforce_case_created ?? {}),
				completed: true,
			},
		});
	};

	const handleAddFee = () => {
		if (!feeStart || !feeEnd || !feeAmount) {
			notifications.error("Please fill in all fee fields");
			return;
		}

		const newFee: FeeStructure = {
			start_range: Number(feeStart),
			end_range: Number(feeEnd),
			fee: Number(feeAmount),
			fee_type: feeType,
		};

		let updatedFees: FeeStructure[];

		if (editingFeeIndex !== null) {
			// Update existing fee
			updatedFees =
				wallet.fee_structures?.map((fee, idx) =>
					idx === editingFeeIndex ? newFee : fee,
				) || [];
		} else {
			// Add new fee
			updatedFees = [...(wallet.fee_structures || []), newFee];
		}

		updateWallet(wallet.id, { fee_structures: updatedFees });

		// Reset form
		setFeeStart("");
		setFeeEnd("");
		setFeeAmount("");
		setFeeType("percentage");
		setShowFeeForm(false);
		setEditingFeeIndex(null);
	};

	const handleEditFee = (index: number) => {
		const fee = wallet.fee_structures?.[index] as FeeStructure | undefined;
		if (fee) {
			const start = fee.start_range ?? fee.startRange ?? 0;
			const end = fee.end_range ?? fee.endRange ?? 0;
			const type = fee.fee_type ?? fee.feeType ?? "percentage";
			setFeeStart(String(start));
			setFeeEnd(String(end));
			setFeeAmount(fee.fee.toString());
			setFeeType(type);
			setEditingFeeIndex(index);
			setShowFeeForm(true);
		}
	};

	const handleDeleteFee = async (index: number) => {
		const ok = await confirm({
			title: "Delete Fee Range",
			message: "Are you sure you want to delete this fee range?",
			confirmLabel: "Delete",
			variant: "danger",
		});

		if (!ok) return;

		const updatedFees =
			wallet.fee_structures?.filter(
				(_: FeeStructure, idx: number) => idx !== index,
			) || [];
		updateWallet(wallet.id, { fee_structures: updatedFees });
	};

	const handlePartnerActivationClick = () => {
		if (!wallet.partner_activated?.completed) {
			setShowActivationModal(true);
		} else {
			handleChecklistToggle("partner_activated", wallet.partner_activated);
		}
	};

	const handleActivationSubmit = (data: {
		agentId: string;
		mainOfficeId: string;
	}) => {
		// Save IDs + mark activated + set Onboarded in a single update to avoid race (IDs being overwritten)
		updateWallet(wallet.id, {
			agent_id: data.agentId,
			main_office_id: data.mainOfficeId,
			partner_activated: {
				...(wallet.partner_activated ?? {}),
				completed: true,
				completed_at: new Date().toISOString(),
				completed_by: user?.name ?? user?.email ?? user?.id,
			},
			onboarding_status: "Onboarded" as OnboardingStatus,
		});
		setShowActivationModal(false);
	};

	const downloadFeeTable = () => {
		if (!wallet.fee_structures || wallet.fee_structures.length === 0) return;

		let csv = "Start Range,End Range,Fee,Type\n";
		wallet.fee_structures?.forEach((fee: FeeStructure) => {
			const feeValue = fee.feeType === "flat" ? `$${fee.fee}` : `${fee.fee}%`;
			const feeTypeLabel = fee.feeType === "flat" ? "Flat" : "Percentage";
			csv += `$${fee.startRange},$${fee.endRange},${feeValue},${feeTypeLabel}\n`;
		});

		const blob = new Blob([csv], { type: "text/csv" });
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `${wallet.wallet_name}-fee-structure.csv`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		window.URL.revokeObjectURL(url);
	};

	return (
		<>
			<PartnerActivationModal
				isOpen={showActivationModal}
				onClose={() => setShowActivationModal(false)}
				onSubmit={handleActivationSubmit}
				walletName={wallet.wallet_name}
			/>

			<div className="flex flex-col gap-4 pt-2">
				{/* STEP 1: Not Started - Document Collection */}
				{viewingStep === "Not Started" && (
					<>
						<div>
							<h3 className="text-lg font-semibold text-(--color-foreground) mb-1">
								Step 1: Required Documents & Actions
							</h3>
							<p className="text-sm text-(--color-muted-foreground)">
								Complete all legal and compliance documents to move to
								"Onboarding in Progress".
							</p>
						</div>

						<div className="flex flex-col gap-2">
							{/* Hide Salesforce Opportunity for partners */}
							{!isPartnerView && wallet.salesforce_opportunity_created && (
								<ChecklistItemComponent
									item={toChecklistItem(
										"salesforce_opportunity_created",
										wallet.salesforce_opportunity_created,
									)}
									onToggle={() =>
										handleChecklistToggle(
											"salesforce_opportunity_created",
											wallet.salesforce_opportunity_created,
										)
									}
									disabled={isUpdating}
								/>
							)}

							{/* KYB Application - MOVED TO FIRST */}
							<ChecklistItemComponent
								item={toChecklistItem(
									"kyc_form_completed",
									wallet.kyc_form_completed,
								)}
								onToggle={() => {}} // Disabled - controlled by progress
								// NO subtasks shown for KYB task
								// NO document upload for KYB task - admin cannot upload
								hideSubtasks={true} // Always hide subtasks for KYB
								customAction={
									<div
										style={{ display: "flex", gap: "8px", marginLeft: "auto" }}
									>
										{!wallet.kyc_form_completed?.kyb_form_id ? (
											// Not started - Show START button
											<Button
												type="button"
												variant="default"
												size="sm"
												onClick={(e) => {
													e.stopPropagation();
													// No status change - label will update dynamically when KYB is created
													navigate(`/kyc/new?walletId=${wallet.id}`);
												}}
											>
												<FileText size={14} />
												Start
											</Button>
										) : wallet.kyc_form_completed?.kyb_progress !== undefined &&
											wallet.kyc_form_completed.kyb_progress < 100 ? (
											// In progress - Show RESUME with step number (walletId in URL for approval sync)
											<Button
												type="button"
												variant="subtle"
												size="sm"
												onClick={(e) => {
													e.stopPropagation();
													navigate(
														`/kyc/${wallet.kyc_form_completed?.kyb_form_id}?walletId=${wallet.id}`,
													);
												}}
											>
												<Edit size={14} />
												Resume (Step{" "}
												{Math.max(
													1,
													Math.min(
														6,
														Math.ceil(
															((wallet.kyc_form_completed.kyb_progress ?? 0) /
																100) *
																6,
														),
													),
												)}{" "}
												of 6)
											</Button>
										) : (
											// Completed - Show VIEW button (walletId in URL for consistency)
											<Button
												type="button"
												variant="outline"
												size="sm"
												onClick={(e) => {
													e.stopPropagation();
													navigate(
														`/kyc/${wallet.kyc_form_completed?.kyb_form_id}?readonly=true&walletId=${wallet.id}`,
													);
												}}
											>
												<Eye size={14} />
												View
											</Button>
										)}
									</div>
								}
							/>

							<ChecklistItemComponent
								item={toChecklistItem("nda_signed", wallet.nda_signed)}
								onToggle={() =>
									handleChecklistToggle("nda_signed", wallet.nda_signed)
								}
								disabled={isPartnerView || isUpdating}
								onDocumentUpload={(file) =>
									handleDocumentUploadWithCompletion("nda_signed", file)
								}
								onDocumentDelete={() => handleDocumentDelete("nda_signed")}
								hideSubtasks={isPartnerView}
							/>

							<ChecklistItemComponent
								item={toChecklistItem(
									"contract_signed",
									wallet.contract_signed,
								)}
								onToggle={() =>
									handleChecklistToggle(
										"contract_signed",
										wallet.contract_signed,
									)
								}
								disabled={isPartnerView || isUpdating}
								onDocumentUpload={(file) =>
									handleDocumentUploadWithCompletion("contract_signed", file)
								}
								onDocumentDelete={() => handleDocumentDelete("contract_signed")}
								hideSubtasks={isPartnerView}
							/>

							{/* Hide Salesforce Case for partners */}
							{!isPartnerView && (
								<ChecklistItemComponent
									item={toChecklistItem(
										"salesforce_case_created",
										wallet.salesforce_case_created,
									)}
									onToggle={() => {
										// Disabled - controlled by entering case number
										notifications.error(
											"Please enter a Salesforce Case Number and click Save to complete this task.",
										);
									}}
									disabled={true}
									customAction={
										!wallet.salesforce_case_created?.completed ? (
											<div
												style={{
													display: "flex",
													gap: "8px",
													alignItems: "center",
													marginLeft: "auto",
												}}
											>
												<input
													type="text"
													value={salesforceCaseInput}
													onChange={(e) =>
														setSalesforceCaseInput(e.target.value)
													}
													placeholder="Enter Case Number"
													className="w-[200px] rounded-lg border border-(--color-border) bg-(--color-card) px-3 py-1.5 text-(--color-foreground) text-sm"
												/>
												<Button
													type="button"
													variant="default"
													size="sm"
													onClick={handleSalesforceCaseSave}
												>
													Save
												</Button>
											</div>
										) : (
											<div
												style={{
													display: "flex",
													alignItems: "center",
													gap: "8px",
													marginLeft: "auto",
												}}
											>
												<span className="font-medium text-(--color-muted-foreground) text-sm">
													Case #: {wallet.salesforce_case_number}
												</span>
												<Button
													type="button"
													variant="destructive"
													size="icon"
													onClick={handleDeleteSalesforceCase}
												>
													<Trash2 size={14} />
												</Button>
											</div>
										)
									}
								/>
							)}

							<p className="text-(--color-muted-foreground) text-xs pl-1">
								✓ When all documents are completed, status will move to
								"Onboarding in Progress"
							</p>
						</div>
					</>
				)}

				{/* STEP 2: Onboarding in Progress - Fee Structure & Activation */}
				{viewingStep === "Onboarding in Progress" && (
					<>
						<div>
							<h3 className="text-lg font-semibold text-(--color-foreground) mb-1">
								Step 2: Partner Fee Structure & Activation
							</h3>
							<p className="text-sm text-(--color-muted-foreground)">
								Set up partner fees and activate the partner with required IDs.
							</p>
						</div>

						{/* Fee Structure Section */}
						<div className="space-y-3">
							<h4 className="text-sm font-semibold text-(--color-foreground)">
								Partner Fee Structure
							</h4>
							<div
								className="rounded-xl bg-(--color-muted) p-4"
								style={{ marginBottom: "16px" }}
							>
								{/* biome-ignore lint/a11y/noLabelWithoutControl: This is a question label, not associated with an input */}
								<label
									style={{
										display: "block",
										marginBottom: "8px",
										fontWeight: "500",
									}}
								>
									{isPartnerView
										? "Will you have your own fees?"
										: "Will partner have their own fees?"}
								</label>
								<div className="flex gap-4">
									<label
										style={{
											display: "flex",
											alignItems: "center",
											gap: "6px",
											cursor: "pointer",
										}}
									>
										<input
											type="radio"
											checked={localHasOwnFees === true}
											onChange={() => handleHasOwnFeesChange(true)}
										/>
										Yes
									</label>
									<label
										style={{
											display: "flex",
											alignItems: "center",
											gap: "6px",
											cursor: "pointer",
										}}
									>
										<input
											type="radio"
											checked={localHasOwnFees === false}
											onChange={() => handleHasOwnFeesChange(false)}
										/>
										No
									</label>
								</div>
							</div>

							{localHasOwnFees && (
								<div className="space-y-4">
									{wallet.fee_structures &&
										wallet.fee_structures.length > 0 && (
											<div
												className="overflow-x-auto rounded-xl border border-(--color-border) p-4"
												style={{ marginBottom: "16px" }}
											>
												<div
													style={{
														display: "flex",
														justifyContent: "space-between",
														alignItems: "center",
														marginBottom: "12px",
													}}
												>
													<h5
														style={{
															margin: 0,
															fontSize: "0.875rem",
															fontWeight: 600,
														}}
													>
														Fee Structure
													</h5>
													<Button
														type="button"
														variant="outline"
														size="sm"
														onClick={downloadFeeTable}
													>
														<Download size={14} />
														Download CSV
													</Button>
												</div>
												<table
													style={{
														fontSize: "0.875rem",
														width: "100%",
														borderCollapse: "separate",
														borderSpacing: 0,
													}}
												>
													<thead>
														<tr>
															<th
																style={{
																	padding: "8px 12px",
																	textAlign: "left",
																}}
															>
																Start Range
															</th>
															<th
																style={{
																	padding: "8px 12px",
																	textAlign: "left",
																}}
															>
																End Range
															</th>
															<th
																style={{
																	padding: "8px 12px",
																	textAlign: "left",
																}}
															>
																Fee
															</th>
															<th
																style={{
																	padding: "8px 12px",
																	textAlign: "left",
																}}
															>
																Type
															</th>
															<th
																style={{
																	width: "120px",
																	padding: "8px 12px",
																	textAlign: "center",
																}}
															>
																Actions
															</th>
														</tr>
													</thead>
													<tbody>
														{wallet.fee_structures.map(
															(fee: FeeStructure, index: number) => (
																<tr
																	key={`fee-${fee.start_range ?? fee.startRange}-${fee.end_range ?? fee.endRange}-${index}`}
																>
																	<td style={{ padding: "8px 12px" }}>
																		${fee.start_range ?? fee.startRange}
																	</td>
																	<td style={{ padding: "8px 12px" }}>
																		${fee.end_range ?? fee.endRange}
																	</td>
																	<td style={{ padding: "8px 12px" }}>
																		{(fee.fee_type ?? fee.feeType) === "flat"
																			? `$${fee.fee}`
																			: `${fee.fee}%`}
																	</td>
																	<td style={{ padding: "8px 12px" }}>
																		{(fee.fee_type ?? fee.feeType) === "flat"
																			? "Flat"
																			: "Percentage"}
																	</td>
																	<td
																		style={{
																			padding: "8px 12px",
																			textAlign: "center",
																		}}
																	>
																		<div
																			style={{
																				display: "flex",
																				gap: "8px",
																				justifyContent: "center",
																			}}
																		>
																			<button
																				type="button"
																				onClick={() => handleEditFee(index)}
																				className="flex cursor-pointer items-center gap-1 rounded-lg border border-(--color-border) bg-transparent px-2 py-1 text-(--color-foreground) transition-colors hover:border-(--color-accent) hover:bg-(--color-muted)"
																				title="Edit"
																			>
																				<Edit size={14} />
																			</button>
																			<button
																				type="button"
																				onClick={() => handleDeleteFee(index)}
																				className="flex cursor-pointer items-center gap-1 rounded-(--radius-xs) bg-(--color-error-500) px-2 py-1 text-white transition-colors hover:bg-(--color-error-600)"
																				title="Delete"
																			>
																				<Trash2 size={14} />
																			</button>
																		</div>
																	</td>
																</tr>
															),
														)}
													</tbody>
												</table>
											</div>
										)}

									{!showFeeForm ? (
										<Button
											type="button"
											variant="outline"
											size="sm"
											onClick={() => setShowFeeForm(true)}
											className="mb-4"
										>
											<Plus size={16} />
											Add Fee Range
										</Button>
									) : (
										<div className="mb-4 rounded-xl bg-(--color-muted) p-4">
											<h5
												style={{
													margin: "0 0 16px 0",
													fontSize: "0.875rem",
													fontWeight: 600,
												}}
											>
												{editingFeeIndex !== null
													? "Edit Fee Range"
													: "Add Fee Range"}
											</h5>
											<div style={{ marginBottom: "12px" }}>
												{/* biome-ignore lint/a11y/noLabelWithoutControl: This is a display label */}
												<label
													style={{
														display: "block",
														fontWeight: 600,
														marginBottom: "8px",
														fontSize: "0.875rem",
													}}
												>
													Fee Type
												</label>
												<div style={{ display: "flex", gap: "16px" }}>
													<label
														style={{
															display: "flex",
															alignItems: "center",
															gap: "6px",
															cursor: "pointer",
															fontSize: "0.875rem",
														}}
													>
														<input
															type="radio"
															checked={feeType === "percentage"}
															onChange={() => setFeeType("percentage")}
														/>
														<span>Percentage (%)</span>
													</label>
													<label
														style={{
															display: "flex",
															alignItems: "center",
															gap: "6px",
															cursor: "pointer",
															fontSize: "0.875rem",
														}}
													>
														<input
															type="radio"
															checked={feeType === "flat"}
															onChange={() => setFeeType("flat")}
														/>
														<span>Flat Fee ($)</span>
													</label>
												</div>
											</div>

											<div
												style={{
													display: "flex",
													gap: "10px",
													marginBottom: "12px",
												}}
											>
												<input
													type="number"
													placeholder="Start ($)"
													value={feeStart}
													onChange={(e) => setFeeStart(e.target.value)}
													className="min-w-0 flex-1 rounded-lg border border-(--color-border) bg-(--color-card) px-3 py-2 text-(--color-foreground) text-sm"
												/>
												<input
													type="number"
													placeholder="End ($)"
													value={feeEnd}
													onChange={(e) => setFeeEnd(e.target.value)}
													className="min-w-0 flex-1 rounded-lg border border-(--color-border) bg-(--color-card) px-3 py-2 text-(--color-foreground) text-sm"
												/>
												<input
													type="number"
													step="0.01"
													placeholder={
														feeType === "percentage" ? "Fee (%)" : "Fee ($)"
													}
													value={feeAmount}
													onChange={(e) => setFeeAmount(e.target.value)}
													className="min-w-0 flex-1 rounded-lg border border-(--color-border) bg-(--color-card) px-3 py-2 text-(--color-foreground) text-sm"
												/>
											</div>
											<div style={{ display: "flex", gap: "8px" }}>
												<Button
													type="button"
													variant="default"
													size="sm"
													onClick={handleAddFee}
												>
													{editingFeeIndex !== null ? "Update" : "Add"}
												</Button>
												<Button
													type="button"
													variant="outline"
													size="sm"
													onClick={() => {
														setShowFeeForm(false);
														setFeeType("percentage");
														setFeeStart("");
														setFeeEnd("");
														setFeeAmount("");
														setEditingFeeIndex(null);
													}}
												>
													Cancel
												</Button>
											</div>
										</div>
									)}
								</div>
							)}
						</div>

						{/* Partner Activation */}
						<div className="space-y-2">
							<h4 className="text-sm font-semibold text-(--color-foreground)">
								Partner Activation
							</h4>
							{!isPartnerView ? (
								// Admin view: Full functionality
								<>
									<p className="text-(--color-muted-foreground) text-xs mb-2">
										Activate the partner by providing Agent ID, Main Office ID,
										and Funding Address.
									</p>

									{/* biome-ignore lint/a11y/noStaticElementInteractions: Clickable div for partner activation */}
									{/* biome-ignore lint/a11y/useKeyWithClickEvents: Keyboard handler not needed */}
									<div
										onClick={handlePartnerActivationClick}
										style={{ cursor: "pointer" }}
									>
										<ChecklistItemComponent
											item={toChecklistItem(
												"partner_activated",
												wallet.partner_activated,
											)}
											onToggle={handlePartnerActivationClick}
											disabled={isUpdating}
										/>
									</div>

									{wallet.agent_id &&
										wallet.main_office_id &&
										wallet.funding_address && (
											<div className="mt-2 rounded-xl bg-(--color-muted) p-3 text-sm">
												<p style={{ margin: "0 0 6px 0" }}>
													<strong>Agent ID:</strong> {wallet.agent_id}
												</p>
												<p style={{ margin: "0 0 6px 0" }}>
													<strong>Main Office ID:</strong>{" "}
													{wallet.main_office_id}
												</p>
												<p style={{ margin: 0 }}>
													<strong>Funding Address:</strong>{" "}
													{wallet.funding_address}
												</p>
											</div>
										)}

									<p className="text-(--color-muted-foreground) text-xs pl-1 mt-1">
										✓ When activated, status will move to "Onboarded"
									</p>
								</>
							) : (
								// Partner view: Read-only display
								<>
									<p className="mb-3 text-(--color-muted-foreground) text-sm">
										Partner activation will be completed by MoneyGram team.
									</p>
									{wallet.agent_id &&
									wallet.main_office_id &&
									wallet.funding_address ? (
										<div className="mt-3 rounded-xl border border-(--color-border) bg-(--color-muted) p-4">
											<h5
												style={{
													margin: "0 0 12px 0",
													fontSize: "0.875rem",
													fontWeight: 600,
												}}
											>
												Activation Information
											</h5>
											<div style={{ fontSize: "0.875rem" }}>
												<p style={{ margin: "0 0 8px 0" }}>
													<strong>Agent ID:</strong> {wallet.agent_id}
												</p>
												<p style={{ margin: "0 0 8px 0" }}>
													<strong>Main Office ID:</strong>{" "}
													{wallet.main_office_id}
												</p>
												<p style={{ margin: 0 }}>
													<strong>Funding Address:</strong>{" "}
													{wallet.funding_address}
												</p>
											</div>
										</div>
									) : (
										<div className="mt-3 rounded-xl bg-(--color-muted) p-4 text-(--color-muted-foreground) italic">
											Activation information pending from MoneyGram team.
										</div>
									)}
								</>
							)}
						</div>
					</>
				)}

				{/* STEP 3: Onboarded */}
				{viewingStep === "Onboarded" && (
					<>
						<div className="flex flex-col gap-4">
							<div className="flex items-center gap-3">
								<CheckCircle size={24} className="shrink-0 text-[#00E6C3]" />
								<h3 className="m-0 font-semibold text-(--color-foreground) text-lg">
									Onboarding Complete!
								</h3>
							</div>
							<p className="m-0 text-(--color-muted-foreground) text-sm">
								All onboarding tasks are complete. Partner is activated and
								ready for production deployment.
							</p>
							{wallet.agent_id &&
								wallet.main_office_id &&
								wallet.funding_address && (
									<div className="space-y-2 rounded-xl bg-(--color-muted) p-4 text-sm">
										<p className="m-0">
											<strong>Agent ID:</strong> {wallet.agent_id}
										</p>
										<p className="m-0">
											<strong>Main Office ID:</strong> {wallet.main_office_id}
										</p>
										<p className="m-0">
											<strong>Funding Address:</strong> {wallet.funding_address}
										</p>
									</div>
								)}
						</div>
					</>
				)}
			</div>
		</>
	);
};

export default OnboardingWorkflow;
